import { Box, FormSearchMultipleSelection, type OptionSelectionProps } from '@shared/components';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { checkIfCombinationExists } from '../../utils';
import { type LearningObjectiveStatementFieldSlotProps } from '../qconfig-single-question-conditions-form-fields/type';

export interface ItemSetQuestionLOSSelectorProps {
  questionSize: number;
  group: Omit<LearningObjectiveStatementFieldSlotProps, 'existingLOS'> & {
    existingLOS: string[][];
  };
}

export default function ItemSetQuestionLOSSelector(
  props: Readonly<ItemSetQuestionLOSSelectorProps>,
) {
  const { questionSize, group } = props;

  const { watch } = useFormContext();

  const selectedLOS: OptionSelectionProps[] = watch('los') ?? [];

  const { limitReach, selectingLastOption } = useMemo(() => {
    const questionSize = parseInt(props.questionSize as unknown as string);

    const limitReach = selectedLOS.length >= questionSize;
    const selectingLastOption = selectedLOS.length === questionSize - 1;

    return { limitReach, selectingLastOption };
  }, [questionSize, selectedLOS]);

  const disableOption = useCallback(
    (option?: OptionSelectionProps | null) => {
      const noOption = option === null;

      const isCombinationExisting = selectingLastOption
        ? checkIfCombinationExists(group.existingLOS, selectedLOS, option)
        : false;

      const isSelected = option && selectedLOS.some((selected) => selected.value === option.value);

      return (noOption || isCombinationExisting || limitReach) && !isSelected;
    },
    [group.existingLOS, selectedLOS, limitReach, selectingLastOption],
  );

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.grey[50],
        borderRadius: theme.shape.rounded(2),
        border: `1px solid ${theme.palette.grey[200]}`,
        p: theme.spacing(4),
      })}
    >
      <FormSearchMultipleSelection
        name='los'
        required
        label='เลือก LOS'
        limitTags={questionSize}
        options={group.hasViewPermission ? group.options : []}
        placeholder={selectedLOS.length > 0 ? '' : 'เลือก LOS'}
        noOptionsText={group.noOptionsText}
        loading={group.loading}
        getOptionDisabled={disableOption}
      />
    </Box>
  );
}
